/**
 * Calculates the relative luminance of a given sRGB color.
 * The formula is defined by the W3C in the WCAG 2.1 guidelines.
 * @see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 *
 * @param color - The color in hexadecimal format (e.g., "#RRGGBB").
 * @returns The relative luminance (a value between 0 and 1).
 */
function getRelativeLuminance(color: string): number {
    const hex = color.startsWith('#') ? color.substring(1) : color;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const srgb = [r, g, b].map(val => val / 255);

    const linearRGB = srgb.map(val => {
        if (val <= 0.04045) {
            return val / 12.92;
        }
        return Math.pow((val + 0.055) / 1.055, 2.4);
    });

    const [R, G, B] = linearRGB;

    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/**
 * Determines whether black ('#000000') or white ('#FFFFFF') provides better
 * contrast against a given background color, based on WCAG 2.1 guidelines.
 *
 * @param bgColor - The background color in hexadecimal format (e.g., "#RRGGBB").
 * @returns '#FFFFFF' or '#000000' for the best contrast.
 */
export default function getBestContrastColor(bgColor: string): '#FFFFFF' | '#000000' {
    const lumWhite = 1;
    const lumBlack = 0;

    const bgLuminance = getRelativeLuminance(bgColor);

    // Calculate contrast ratio against white and black
    // Formula: (L1 + 0.05) / (L2 + 0.05), where L1 is the lighter color.
    const contrastWithWhite = (lumWhite + 0.05) / (bgLuminance + 0.05);
    const contrastWithBlack = (bgLuminance + 0.05) / (lumBlack + 0.05);

    return contrastWithWhite > contrastWithBlack ? '#FFFFFF' : '#000000';
}
